/* Rivorine cart drawer suggestion card.
   Ported from the Nirole/Povelio cart drawer pattern (see cart-drawer.md), adapted to the
   store's actual theme markup (Denim/Lumin-family: <cart-drawer>, .drawer__inner replaced
   wholesale on every cart change via CartDrawer.renderContents()).

   Real products only (window.RivCartData, generated from the live catalogue). Prices are
   fetched live from /products/<handle>.js at render time, never baked in. */

(function () {
  'use strict';

  var ROTATE_MS = 4200;
  var SESSION_KEY = 'rivSuggestManual';
  var state = {
    picks: [],       // [{handle,title,area}]
    idx: 0,
    timer: null,
    cache: {},        // handle -> live product json
    writing: false     // guards our own mutation from retriggering the observer
  };

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return '€' + parts[0] + ',' + parts[1];
  }

  function withWidth(url, w) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function getProduct(handle) {
    if (state.cache[handle]) return Promise.resolve(state.cache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) { state.cache[handle] = p; return p; });
  }

  // Builds a priority-ordered candidate list of RELATED products, most relevant first:
  //   1. the hand-curated complementary map (window.RivCartData.related, same map used on
  //      product pages for "Vous aimerez aussi"), walked from the most-recently-added cart
  //      item to the oldest, so the suggestion complements what was just added first;
  //   2. any other product sharing a category ("area") with something in the cart;
  //   3. the rest of the catalogue, as a last-resort fallback so the slot is never empty.
  // Never includes a handle already in the cart. Availability is checked later (resolvePicks)
  // against the live /products/<handle>.js response, not here.
  function pickSuggestions(cartItems, byHandle) {
    var cartHandleSet = {};
    cartItems.forEach(function (it) { cartHandleSet[it.handle] = true; });

    // most-recently-added first, de-duplicated
    var recentFirst = [];
    var seenCart = {};
    for (var i = cartItems.length - 1; i >= 0; i--) {
      var h = cartItems[i].handle;
      if (!seenCart[h]) { seenCart[h] = true; recentFirst.push(h); }
    }

    var related = (window.RivCartData && window.RivCartData.related) || {};
    var rows = (window.RivCartData && window.RivCartData.products) || [];

    var candidates = [];
    var added = {};
    function addCandidate(h) {
      if (!h || cartHandleSet[h] || added[h] || !byHandle[h]) return;
      added[h] = true;
      candidates.push(h);
    }

    // 1. curated complementary picks, most recently added cart item first
    recentFirst.forEach(function (h) {
      (related[h] || []).forEach(addCandidate);
    });

    // 2. same-category fallback
    var cartAreas = [];
    recentFirst.forEach(function (h) {
      var row = byHandle[h];
      if (row && cartAreas.indexOf(row[4]) === -1) cartAreas.push(row[4]);
    });
    rows.forEach(function (r) {
      if (cartAreas.indexOf(r[4]) !== -1) addCandidate(r[0]);
    });

    // 3. anything else, so the card is never empty
    rows.forEach(function (r) { addCandidate(r[0]); });

    return candidates.map(function (h) {
      var row = byHandle[h];
      return { handle: h, title: row ? row[1] : h, area: row ? row[4] : '' };
    });
  }

  // Walks the candidate list in priority order, keeping only picks that resolve to a real,
  // available product (skips 404s from unpublished products and out-of-stock items), until
  // `max` picks are collected or the candidates run out.
  function resolvePicks(candidates, max) {
    var results = [];
    var i = 0;
    function next() {
      if (results.length >= max || i >= candidates.length) return Promise.resolve(results);
      var cand = candidates[i++];
      return getProduct(cand.handle).then(function (product) {
        if (product && product.available !== false) results.push(cand);
        return next();
      }).catch(function () { return next(); });
    }
    return next();
  }

  function variantForSelection(product, selection) {
    return product.variants.filter(function (v) {
      return product.options.every(function (opt, i) {
        var sel = selection[opt.name];
        return !sel || v.options[i] === sel;
      });
    })[0] || product.variants[0];
  }

  function renderCard(root, product) {
    var selection = {};
    var realOptions = product.options.filter(function (o) { return o.name !== 'Title'; });

    function currentVariant() {
      return variantForSelection(product, selection) || product.variants[0];
    }

    function paint() {
      var v = currentVariant();
      var img = (v && v.featured_image && v.featured_image.src) || product.featured_image || '';
      var priceEl = root.querySelector('.riv-sg__p');
      var phImg = root.querySelector('.riv-sg__ph img');
      if (phImg) phImg.src = withWidth(img, 200);
      if (priceEl) priceEl.textContent = formatMoney(v ? v.price : product.price);
      root.dataset.variantId = v ? v.id : product.variants[0].id;
    }

    var selectsHtml = '';
    if (realOptions.length > 0 && product.variants.length > 1) {
      selectsHtml = '<div class="riv-sg__vsel">' + realOptions.map(function (opt, i) {
        var options = opt.values.map(function (val) {
          return '<option value="' + escapeHtml(val) + '">' + escapeHtml(val) + '</option>';
        }).join('');
        return '<label><select data-opt="' + escapeHtml(opt.name) + '" data-opt-index="' + i + '">' + options + '</select></label>';
      }).join('') + '</div>';
    }

    root.innerHTML =
      '<div class="riv-sg__ph"><img alt="' + escapeHtml(product.title) + '" loading="lazy"></div>' +
      '<div class="riv-sg__m">' +
      '<p class="riv-sg__t">Voulez-vous ajouter <span class="riv-sg__name">' + escapeHtml(product.title) + '</span> ?</p>' +
      selectsHtml +
      '<div class="riv-sg__row">' +
      '<span class="riv-sg__p">...</span>' +
      '<button type="button" class="riv-sg__btn">Ajouter au panier</button>' +
      '</div>' +
      '</div>';

    realOptions.forEach(function (opt) {
      selection[opt.name] = opt.values[0];
    });

    var selects = root.querySelectorAll('select[data-opt]');
    for (var i = 0; i < selects.length; i++) {
      selects[i].addEventListener('change', function (e) {
        selection[e.target.getAttribute('data-opt')] = e.target.value;
        paint();
      });
    }

    paint();

    var btn = root.querySelector('.riv-sg__btn');
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      addToCart(root);
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function addToCart(cardEl) {
    var variantId = cardEl.dataset.variantId;
    if (!variantId || !window.routes || !window.routes.cart_add_url) return;

    var wrap = cardEl.closest('.riv-sg__card');
    if (wrap) wrap.classList.add('is-busy');

    var cartDrawerEl = document.querySelector('cart-drawer');
    var sectionIds = cartDrawerEl && cartDrawerEl.getSectionsToRender
      ? cartDrawerEl.getSectionsToRender().map(function (s) { return s.id; })
      : ['cart-drawer', 'cart-icon-bubble'];

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) return; // error response, leave drawer as-is
        if (cartDrawerEl && cartDrawerEl.classList.contains('is-empty')) {
          cartDrawerEl.classList.remove('is-empty');
        }
        if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
          cartDrawerEl.renderContents(response);
        }
      })
      .catch(function () {
        if (wrap) wrap.classList.remove('is-busy');
      });
  }

  function renderDots(host, count, activeIdx) {
    if (count <= 1) return;
    var dots = document.createElement('div');
    dots.className = 'riv-sg__dots';
    for (var i = 0; i < count; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      if (i === activeIdx) b.className = 'is-on';
      b.addEventListener('click', function (idx) {
        return function (e) {
          e.preventDefault();
          e.stopPropagation();
          try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (err) {}
          clearInterval(state.timer);
          state.idx = idx;
          paintCurrent(host);
        };
      }(i));
      dots.appendChild(b);
    }
    host.appendChild(dots);
  }

  function paintCurrent(root) {
    if (!state.picks.length) return;
    var pick = state.picks[state.idx];
    var card = document.createElement('div');
    card.className = 'riv-sg__card';
    root.innerHTML = '';
    root.appendChild(card);
    getProduct(pick.handle).then(function (product) {
      renderCard(card, product);
      renderDots(root, state.picks.length, state.idx);
    }).catch(function () {});
  }

  function startRotation(root) {
    clearInterval(state.timer);
    var manual = false;
    try { manual = sessionStorage.getItem(SESSION_KEY) === '1'; } catch (err) {}
    if (manual || state.picks.length <= 1) return;
    state.timer = setInterval(function () {
      state.idx = (state.idx + 1) % state.picks.length;
      paintCurrent(root);
    }, ROTATE_MS);
  }

  function mount() {
    var root = document.getElementById('RivSuggest');
    if (!root) return;

    getCart().then(function (cart) {
      if (!cart.items.length || cart.item_count === 0) {
        root.hidden = true;
        root.innerHTML = '';
        return;
      }

      var data = (window.RivCartData && window.RivCartData.products) || [];
      var byHandle = {};
      data.forEach(function (r) { byHandle[r[0]] = r; });

      var candidates = pickSuggestions(cart.items, byHandle);
      if (!candidates.length) {
        root.hidden = true;
        root.innerHTML = '';
        return;
      }

      return resolvePicks(candidates, 3).then(function (picks) {
        state.picks = picks;
        state.idx = 0;

        if (!state.picks.length) {
          root.hidden = true;
          root.innerHTML = '';
          return;
        }

        root.hidden = false;
        paintCurrent(root);
        startRotation(root);
      });
    }).catch(function () {
      if (root) root.hidden = true;
    });
  }

  function safeMount() {
    if (state.writing) return;
    try {
      state.writing = true;
      mount();
    } catch (e) {
      var root = document.getElementById('RivSuggest');
      if (root) root.hidden = true;
    } finally {
      setTimeout(function () { state.writing = false; }, 0);
    }
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  var debouncedMount = debounce(safeMount, 250);

  function observeDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer || !window.MutationObserver) return;
    var observer = new MutationObserver(function (mutations) {
      if (state.writing) return;
      // ignore mutations that originate inside our own mount, so we don't loop on ourselves
      var external = mutations.some(function (m) {
        return !m.target.closest || !m.target.closest('#RivSuggest');
      });
      if (external) debouncedMount();
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    observeDrawer();
    if (document.getElementById('RivSuggest')) safeMount();
  }

  document.addEventListener('cart:open', debouncedMount);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
